import React, { memo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ProfileDropdown from './Auth/ProfileDropdown';
import './Header.css';

interface JobFilters {
  search: string;
  countries: string[];
  industries: string[];
  jobTypes: string[];
  positions: string[];
  salary: [number, number];
}

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: number;
  employmentType: string;
  remote: boolean;
  coordinates: [number, number];
  industry: string;
  country: string;
  costOfLiving?: number;
}

interface HeaderProps {
  darkMode: boolean;
  activePage: string;
  onNavigate: (page: string) => void;
  onMobileMenuToggle?: () => void;
  showMobileMenu?: boolean;
  filters?: JobFilters;
  filteredJobsCount?: number;
  totalJobsCount?: number;
  companiesCount?: number;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
}

const Header: React.FC<HeaderProps> = memo(({ 
  darkMode, 
  activePage, 
  onNavigate, 
  onMobileMenuToggle,
  showMobileMenu = false,
  filters,
  filteredJobsCount = 0,
  totalJobsCount = 0,
  companiesCount = 0,
  onLoginClick,
  onSignupClick
}) => {
  const { isAuthenticated, user } = useAuth();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  
  // Format numbers with commas
  const formatNumber = (num: number) => num.toLocaleString();
  
  // Get active filters count
  const getActiveFiltersCount = () => {
    if (!filters) return 0;
    let count = 0;
    if (filters.search) count++;
    if (filters.countries.length > 0) count++;
    if (filters.industries.length > 0) count++;
    if (filters.jobTypes.length > 0) count++;
    if (filters.positions.length > 0) count++;
    if (filters.salary[0] > 0 || filters.salary[1] < 200000) count++;
    return count;
  };

  const getPageTitle = () => {
    switch (activePage) {
      case 'landing':
        return 'Welcome to Jobometry';
      case 'jobs':
        return 'Job Search';
      case 'companies':
        return 'Companies';
      case 'salaries':
        return 'Salary Explorer';
      case 'profile':
        return 'Profile Settings';
      default:
        return 'Job Market Analytics';
    }
  };

  const getPageSubtitle = () => {
    switch (activePage) {
      case 'landing':
        return 'Find Your Perfect Job Match';
      case 'jobs':
        const activeFilters = getActiveFiltersCount();
        return activeFilters > 0 
          ? `${formatNumber(filteredJobsCount)} of ${formatNumber(totalJobsCount)} jobs • ${activeFilters} filter${activeFilters !== 1 ? 's' : ''} active`
          : `${formatNumber(totalJobsCount)} jobs available`;
      case 'companies':
        return `${formatNumber(companiesCount)} companies • Global hiring data`;
      case 'salaries':
        return 'Comprehensive salary insights and market trends';
      case 'profile':
        return 'Manage your account and preferences';
      default:
        return 'Where Data Meets Opportunity';
    }
  };

  return (
    <div className={`header-container ${darkMode ? 'dark' : 'light'}`}>
      <header className={`header ${darkMode ? 'dark' : 'light'}`}>
        {onMobileMenuToggle && (
          <button 
            className="mobile-menu-btn"
            onClick={onMobileMenuToggle}
            aria-label="Toggle sidebar"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 6h14M3 12h14M3 18h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        )}
        <div className="logo">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Jobometry</span>
        </div>
        
        <ul className="nav-tabs">
          <li 
            className={`nav-tab ${activePage === 'landing' ? 'active' : ''}`}
            onClick={() => onNavigate('landing')}
          >
            <span className="nav-text">Home</span>
          </li>
          <li 
            className={`nav-tab ${activePage === 'jobs' ? 'active' : ''}`}
            onClick={() => onNavigate('jobs')}
          >
            <span className="nav-text">Job Search</span>
            {activePage === 'jobs' && filteredJobsCount > 0 && (
              <span className="nav-count">{formatNumber(filteredJobsCount)}</span>
            )}
          </li>
          <li 
            className={`nav-tab ${activePage === 'companies' ? 'active' : ''}`}
            onClick={() => onNavigate('companies')}
          >
            <span className="nav-text">Companies</span>
            {activePage === 'companies' && companiesCount > 0 && (
              <span className="nav-count">{formatNumber(companiesCount)}</span>
            )}
          </li>
          <li 
            className={`nav-tab ${activePage === 'salaries' ? 'active' : ''}`}
            onClick={() => onNavigate('salaries')}
          >
            <span className="nav-text">Salary Explorer</span>
          </li>
        </ul>
        
        <div className="header-right">
          <div className="header-stats">
            <div className="current-view">
              <span className="view-title">{getPageTitle()}</span>
              <span className="view-subtitle">{getPageSubtitle()}</span>
            </div>
          </div>
          
          <div className="auth-buttons">
            {!isAuthenticated ? (
              <>
                <button 
                  className="auth-btn login-btn"
                  onClick={onLoginClick}
                >
                  Login
                </button>
                <button 
                  className="auth-btn signup-btn"
                  onClick={onSignupClick}
                >
                  Sign Up
                </button>
              </>
            ) : (
              <div className="profile-dropdown-container">
                <button 
                  className="profile-btn"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  aria-label="User profile menu"
                >
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.name} className="profile-avatar" />
                  ) : (
                    <div className="profile-avatar-placeholder">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                </button>
                
                <ProfileDropdown 
                  isOpen={showProfileDropdown}
                  onClose={() => setShowProfileDropdown(false)}
                  onNavigate={onNavigate}
                  darkMode={darkMode}
                />
              </div>
            )}
          </div>
        </div>
      </header>
    </div>
  );
});

Header.displayName = 'Header';

export default Header;